/**
 * OverheadReportPage — 149-cw-r133 (2026-08-31).
 *
 * Halaman "Laporan Overhead Kantor" — laporan biaya operasional kantor
 * (bukan biaya proyek client). Filter periode, kategori, proyek + export.
 *
 * Access gate: `finance_report.view`.
 *
 * Fitur:
 *  - Filter: date range (from/to), kategori, proyek, toggle include petty cash
 *  - 4 KPI cards (Total, Trx, Rata-rata/bulan, Kategori terbesar)
 *  - 3 tabel: per Kategori, per Bulan (dengan mini bar), per Proyek
 *  - Rincian transaksi (line items)
 *  - Export Excel + Print PDF
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, Calendar, Download, FileText, Filter, PieChart, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import api from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { exportOverheadToExcel, printOverheadToPDF } from "@/lib/overheadExport";

const CATEGORIES = ["Material", "Transport", "Konsumsi", "Utilitas", "Peralatan/Sewa", "Fee/Subcont", "Biaya Kantor", "Pajak/Retribusi", "Lain-lain"];

function firstDayOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function lastDayOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}
function fmtMonthLabel(ym) {
  if (!ym) return "-";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  return `${months[Number(m) - 1] || m} ${y}`;
}

export default function OverheadReportPage() {
  const { user } = useAuth();
  const { canPerm, ready } = useAccess();
  const canView = canPerm(user, "finance_report", "view");

  const [dateFrom, setDateFrom] = useState(firstDayOfMonth(-11));
  const [dateTo, setDateTo] = useState(lastDayOfMonth(0));
  const [category, setCategory] = useState("");
  const [projectId, setProjectId] = useState("");
  const [includePetty, setIncludePetty] = useState(true);
  const [projects, setProjects] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const officeProjects = useMemo(
    () => (projects || []).filter((p) => p.project_type === "internal"),
    [projects]
  );

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/active-projects");
      setProjects(Array.isArray(data) ? data : []);
    } catch { setProjects([]); }
  }, []);

  const loadReport = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (category) params.set("category", category);
      if (projectId) params.set("project_id", projectId);
      params.set("include_petty", includePetty ? "true" : "false");
      const { data } = await api.get(`/finance/overhead-report?${params.toString()}`);
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat laporan overhead");
      setReport(null);
    } finally { setLoading(false); }
  }, [canView, dateFrom, dateTo, category, projectId, includePetty]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => {
    const h = () => loadReport();
    window.addEventListener("ens:refresh", h);
    return () => window.removeEventListener("ens:refresh", h);
  }, [loadReport]);

  if (!ready) return <div className="p-6 text-sm italic text-slate-400">Memuat izin akses…</div>;
  if (!canView) {
    return (
      <div className="p-6" data-testid="overhead-no-access">
        <div className="max-w-md mx-auto mt-16 border border-rose-200 bg-rose-50 p-6 text-center">
          <AlertTriangle size={28} className="text-rose-600 mx-auto mb-2" />
          <h2 className="font-display text-lg font-bold text-rose-800 mb-1">Akses Ditolak</h2>
          <p className="text-xs text-rose-700 leading-relaxed">
            Anda belum punya izin untuk melihat <b>Laporan Overhead Kantor</b>.
            Minta admin mengaktifkan permission <code className="font-mono bg-white/70 px-1">finance_report.view</code>.
          </p>
        </div>
      </div>
    );
  }

  const s = report?.summary || {};
  const maxMonthTotal = Math.max(1, ...(report?.by_month || []).map((m) => m.total));

  return (
    <div className="p-6 space-y-4" data-testid="overhead-report-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            Keuangan
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight mt-1 font-display flex items-center gap-2">
            <PieChart size={26} className="text-[#0B1D3A]" /> Laporan Overhead Kantor
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Biaya operasional kantor (bukan proyek client). Termasuk expense di project internal + kategori "Biaya Kantor" / "Utilitas" dari proyek apapun.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={loadReport}
            data-testid="overhead-refresh"
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button" onClick={() => report && exportOverheadToExcel(report, `overhead-${dateFrom}_${dateTo}.xlsx`)}
            disabled={!report || loading}
            data-testid="overhead-export-excel"
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-emerald-700 hover:bg-emerald-800 text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
          >
            <Download size={13} /> Excel
          </button>
          <button
            type="button" onClick={() => report && printOverheadToPDF(report)}
            disabled={!report || loading}
            data-testid="overhead-print-pdf"
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-rose-700 hover:bg-rose-800 text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
          >
            <FileText size={13} /> Print PDF
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border border-slate-200 bg-white p-4 space-y-3" data-testid="overhead-filter-bar">
        <div className="flex items-center gap-2 text-slate-700 text-[11px] uppercase tracking-wider font-semibold">
          <Filter size={13} /> Filter Periode & Kategori
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1">
            <label className="overline text-slate-600 text-[10px]">Dari Tanggal</label>
            <div className="relative">
              <Calendar size={12} className="absolute left-2 top-3 text-slate-400" />
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="overhead-date-from"
                className="w-full h-9 pl-7 pr-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="overline text-slate-600 text-[10px]">Sampai Tanggal</label>
            <div className="relative">
              <Calendar size={12} className="absolute left-2 top-3 text-slate-400" />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="overhead-date-to"
                className="w-full h-9 pl-7 pr-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="overline text-slate-600 text-[10px]">Kategori</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="overhead-category"
              className="w-full h-9 px-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none bg-white">
              <option value="">Semua kategori</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Petty Cash">Petty Cash</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="overline text-slate-600 text-[10px]">Proyek/Kantor</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="overhead-project"
              className="w-full h-9 px-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none bg-white">
              <option value="">Semua proyek/kantor</option>
              <optgroup label="Kantor / Internal">
                {officeProjects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </optgroup>
              <optgroup label="Proyek (untuk overhead misplaced)">
                {projects.filter((p) => p.project_type !== "internal").map((p) => (
                  <option key={p.id} value={p.id}>{p.project_name || p.wo_number}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className="space-y-1 flex flex-col justify-end">
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 h-9 cursor-pointer">
              <input type="checkbox" checked={includePetty} onChange={(e) => setIncludePetty(e.target.checked)} data-testid="overhead-include-petty"
                className="accent-[#0B1D3A]" />
              Sertakan Petty Cash
            </label>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
          <button type="button" onClick={() => { setDateFrom(firstDayOfMonth(0)); setDateTo(lastDayOfMonth(0)); }}
            data-testid="overhead-preset-thisMonth"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Bulan Ini
          </button>
          <button type="button" onClick={() => { setDateFrom(firstDayOfMonth(-1)); setDateTo(lastDayOfMonth(-1)); }}
            data-testid="overhead-preset-lastMonth"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Bulan Lalu
          </button>
          <button type="button" onClick={() => { setDateFrom(firstDayOfMonth(-2)); setDateTo(lastDayOfMonth(0)); }}
            data-testid="overhead-preset-3m"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            3 Bulan
          </button>
          <button type="button" onClick={() => { setDateFrom(firstDayOfMonth(-11)); setDateTo(lastDayOfMonth(0)); }}
            data-testid="overhead-preset-12m"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            12 Bulan
          </button>
          <button type="button" onClick={() => { setDateFrom(`${new Date().getFullYear()}-01-01`); setDateTo(`${new Date().getFullYear()}-12-31`); }}
            data-testid="overhead-preset-ytd"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Tahun Berjalan
          </button>
          <button type="button" onClick={() => { setCategory(""); setProjectId(""); setIncludePetty(true); }}
            data-testid="overhead-reset"
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]">
            Reset filter
          </button>
        </div>
      </div>

      {loading && <div className="text-xs text-slate-400 italic">Memuat laporan…</div>}

      {report && !loading && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Pengeluaran" value={formatIDR(s.total)} tone="rose" testid="overhead-kpi-total" hint={`${s.count || 0} transaksi`} />
            <KpiCard label="Rata-rata / Bulan" value={formatIDR(s.avg_monthly)} tone="navy" testid="overhead-kpi-avg" hint={`Selama ${s.period_months || 0} bulan`} />
            <KpiCard label="Kategori Terbesar" value={s.top_category || "-"} tone="amber" testid="overhead-kpi-top-cat" hint={s.top_amount ? formatIDR(s.top_amount) : "-"} isText />
            <KpiCard label="Periode" value={`${s.date_from || "?"} → ${s.date_to || "?"}`} tone="slate" testid="overhead-kpi-period" hint="Filter aktif" isText />
          </div>

          {/* By category + by month grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SectionCard title="Per Kategori" testid="overhead-by-category">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b-2 border-slate-300">
                  <tr>
                    <th className="text-left px-3 py-2 overline text-slate-700">Kategori</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">Total</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">Trx</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.by_category || []).length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic">Tidak ada data</td></tr>
                  )}
                  {(report.by_category || []).map((c) => (
                    <tr key={c.category} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`overhead-cat-row-${c.category}`}>
                      <td className="px-3 py-2 text-slate-800 font-semibold">{c.category}</td>
                      <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(c.total)}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-slate-600">{c.count}</td>
                      <td className="px-3 py-2 tabular-nums text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-16 bg-slate-100">
                            <div className="h-1.5 bg-[#0B1D3A]" style={{ width: `${Math.min(100, c.pct)}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-600 tabular-nums w-10 text-right">{c.pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="Per Bulan" testid="overhead-by-month">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b-2 border-slate-300">
                  <tr>
                    <th className="text-left px-3 py-2 overline text-slate-700">Bulan</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">Total</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">Trx</th>
                    <th className="text-left px-3 py-2 overline text-slate-700 w-1/3">Distribusi</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.by_month || []).length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic">Tidak ada data</td></tr>
                  )}
                  {(report.by_month || []).map((m) => (
                    <tr key={m.ym} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`overhead-month-row-${m.ym}`}>
                      <td className="px-3 py-2 text-slate-800 font-semibold whitespace-nowrap">{fmtMonthLabel(m.ym)}</td>
                      <td className="px-3 py-2 tabular-nums text-right font-bold text-[#0B1D3A]">{formatIDR(m.total)}</td>
                      <td className="px-3 py-2 tabular-nums text-right text-slate-600">{m.count}</td>
                      <td className="px-3 py-2">
                        <div className="h-2 bg-slate-100 overflow-hidden">
                          <div className="h-2 bg-gradient-to-r from-[#0B1D3A] to-[#122a4d]" style={{ width: `${(m.total / maxMonthTotal * 100).toFixed(1)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          </div>

          {/* By Project */}
          <SectionCard title="Per Proyek / Kantor" testid="overhead-by-project">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b-2 border-slate-300">
                <tr>
                  <th className="text-left px-3 py-2 overline text-slate-700">Proyek / Kantor</th>
                  <th className="text-right px-3 py-2 overline text-slate-700">Total</th>
                  <th className="text-right px-3 py-2 overline text-slate-700">Trx</th>
                </tr>
              </thead>
              <tbody>
                {(report.by_project || []).length === 0 && (
                  <tr><td colSpan={3} className="text-center py-6 text-slate-400 italic">Tidak ada data</td></tr>
                )}
                {(report.by_project || []).map((p, i) => (
                  <tr key={`${p.project_id || "petty"}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`overhead-proj-row-${i}`}>
                    <td className="px-3 py-2 text-slate-800">
                      <Building2 size={12} className="inline mr-1.5 text-slate-500" />
                      {p.project_name}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(p.total)}</td>
                    <td className="px-3 py-2 tabular-nums text-right text-slate-600">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>

          {/* Line items */}
          <SectionCard title={`Daftar Transaksi (${(report.line_items || []).length})`} testid="overhead-line-items">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b-2 border-slate-300">
                  <tr>
                    <th className="text-left px-3 py-2 overline text-slate-700 whitespace-nowrap">Tanggal</th>
                    <th className="text-left px-3 py-2 overline text-slate-700">Proyek / Kantor</th>
                    <th className="text-left px-3 py-2 overline text-slate-700">Kategori</th>
                    <th className="text-right px-3 py-2 overline text-slate-700">Nominal</th>
                    <th className="text-left px-3 py-2 overline text-slate-700">Referensi</th>
                    <th className="text-left px-3 py-2 overline text-slate-700">Catatan</th>
                    <th className="text-left px-3 py-2 overline text-slate-700">Oleh</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.line_items || []).length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Tidak ada transaksi sesuai filter.</td></tr>
                  )}
                  {(report.line_items || []).slice(0, 300).map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`overhead-item-${r.id}`}>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{r.date ? fmtDate(r.date) : "-"}</td>
                      <td className="px-3 py-2 text-slate-800">
                        <div className="font-semibold">{r.project_name}</div>
                        {r.source === "petty" && <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 mt-0.5 inline-block">Petty</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="inline-block px-1.5 py-0.5 text-[10px] bg-slate-100 border border-slate-300 text-slate-700 uppercase tracking-wider font-semibold">{r.category}</span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(r.amount)}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{r.ref || "-"}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[240px] truncate" title={r.note}>{r.note || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{r.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(report.line_items || []).length > 300 && (
                <div className="p-2 text-[11px] text-slate-500 italic text-center border-t border-slate-100">
                  Menampilkan 300 dari {(report.line_items || []).length} baris. Export Excel untuk daftar lengkap.
                </div>
              )}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, tone, testid, hint, isText }) {
  const border =
    tone === "rose" ? "border-l-rose-500" :
    tone === "navy" ? "border-l-[#0B1D3A]" :
    tone === "amber" ? "border-l-amber-500" :
    "border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${border} p-3`} data-testid={testid}>
      <div className="overline text-slate-600 text-[10px]">{label}</div>
      <div className={`font-display font-bold text-[#0B1D3A] mt-1 ${isText ? "text-sm" : "text-lg tabular-nums"}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function SectionCard({ title, testid, children }) {
  return (
    <div className="border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-[11px] uppercase tracking-wider font-bold text-[#0B1D3A]">{title}</div>
      </div>
      {children}
    </div>
  );
}
